export interface TranslationNode {
  textContent: string | null
  isConnected: boolean
}

interface TextState {
  source: string
  rendered: string
  translated: boolean
}

export function splitTranslationText(text: string): string[] {
  const parts: string[] = []
  while (text.length > 1800) {
    let end = text.lastIndexOf(' ', 1800)
    if (end < 900)
      end = 1800
    const last = text.charCodeAt(end - 1)
    if (last >= 0xD800 && last <= 0xDBFF)
      end--
    parts.push(text.slice(0, end))
    text = text.slice(end)
  }
  if (text)
    parts.push(text)
  return parts
}

export function createTranslationSession() {
  const originals = new Map<TranslationNode, TextState>()
  let revision = 0

  function prepare(nodes: TranslationNode[]) {
    for (const node of originals.keys()) {
      if (!node.isConnected)
        originals.delete(node)
    }
    return nodes.flatMap((node) => {
      const text = node.textContent || ''
      let state = originals.get(node)
      if (!state || state.rendered !== text) {
        state = { source: text, rendered: text, translated: false }
        originals.set(node, state)
      }
      return state.translated ? [] : [{ node, source: state.source, revision }]
    })
  }

  function apply(batch: ReturnType<typeof prepare>, translations: string[]) {
    if (translations.length !== batch.length || translations.some(text => typeof text !== 'string' || !text.trim()))
      throw new Error('Translation response is incomplete.')
    batch.forEach(({ node, source, revision: requestedRevision }, index) => {
      const state = originals.get(node)
      const translated = translations[index]
      if (requestedRevision !== revision || !node.isConnected || !state || node.textContent !== source || translated === undefined)
        return
      node.textContent = translated
      state.rendered = translated
      state.translated = true
    })
  }

  function reset() {
    revision++
    for (const [node, state] of originals) {
      // Vue may have updated the node while translation was in flight.
      if (node.isConnected && node.textContent === state.rendered)
        node.textContent = state.source
    }
    originals.clear()
  }

  return {
    prepare,
    apply,
    reset,
    get revision() {
      return revision
    },
  }
}
